import isoCountries from 'i18n-iso-countries';
import type { LabelledEntity } from 'shared/types/commonView';

/** ISO 3166-1 alpha-3 → alpha-2, the same list the API validates country codes with. */
const alpha2ByAlpha3: Record<string, string> = isoCountries.getAlpha3Codes();

const regionNamesByLocale = new Map<string, Intl.DisplayNames | null>();

/** Country names in the app's language, from the browser (`pl`, `pt_PT` → `pt-PT`). */
function getRegionNames(locale: string): Intl.DisplayNames | null {
  if (!regionNamesByLocale.has(locale)) {
    try {
      regionNamesByLocale.set(locale, new Intl.DisplayNames([locale.replace('_', '-')], { type: 'region' }));
    } catch {
      regionNamesByLocale.set(locale, null);
    }
  }
  return regionNamesByLocale.get(locale) ?? null;
}

/** The country's name in `locale`; the code itself when it is unknown. */
export function getCountryName(alpha3: string, locale: string): string {
  const alpha2 = alpha2ByAlpha3[alpha3.toUpperCase()];
  return (alpha2 && getRegionNames(locale)?.of(alpha2)) || alpha3;
}

/** The country as a select option: `_id` is the ISO alpha-3 code. */
export function toCountryOption(alpha3: string, locale: string): LabelledEntity {
  return { _id: alpha3, label: getCountryName(alpha3, locale) };
}

/** Every country as a select option, sorted by name in `locale`. */
export function getCountryOptions(locale: string): LabelledEntity[] {
  return Object.keys(alpha2ByAlpha3)
    .map((alpha3) => toCountryOption(alpha3, locale))
    .sort((first, second) => first.label.localeCompare(second.label, locale.replace('_', '-')));
}
